import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  Activity,
  BarChart3,
  CheckCircle2,
  ClipboardList,
  Eye,
  EyeOff,
  Loader2,
  NotebookPen,
  Shield,
  TrendingUp,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BrandLogo } from "../components/BrandLogo";
import { LoadingScreen } from "../components/LoadingScreen";
import { api } from "../lib/api";
import { cn } from "../lib/utils";

const BAR_HEIGHTS = [38, 62, 48, 78, 58, 88, 72];
const WEEK_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const FEATURES = [
  {
    icon: NotebookPen,
    label: "Daily logs",
    desc: "End-of-day accountability",
    accent: "border-emerald-400/30 bg-emerald-500/15 text-emerald-50",
  },
  {
    icon: ClipboardList,
    label: "Tasks",
    desc: "Assignments & KPI tracking",
    accent: "border-white/20 bg-white/10 text-white",
  },
  {
    icon: BarChart3,
    label: "KPI monitoring",
    desc: "Green / amber / red status",
    accent: "border-amber-400/30 bg-amber-500/12 text-amber-50",
  },
];

function AuthShowcase() {
  const reduceMotion = useReducedMotion();

  return (
    <div className="relative flex h-full flex-col overflow-hidden p-10 xl:p-14">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <BrandLogo size="xl" variant="white" to={null} />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, delay: 0.08 }}
        className="relative z-10 mt-10 max-w-lg"
      >
        <span className="mb-4 inline-flex rounded-md border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium text-white/90">
          Workforce accountability
        </span>
        <h1 className="font-display text-3xl font-semibold leading-tight text-white xl:text-4xl">
          Bringing clarity to your team&apos;s daily work
        </h1>
        <p className="mt-4 text-base leading-relaxed text-white/75">
          Track work logs, tasks, and KPIs in one calm, focused workspace — built for healthcare leadership and the
          people they support.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          {FEATURES.map(({ icon: Icon, label, desc, accent }, i) => (
            <motion.span
              key={label}
              title={desc}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 + i * 0.06 }}
              className={cn(
                "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium backdrop-blur-sm",
                accent,
              )}
            >
              <Icon className="h-3.5 w-3.5 shrink-0 opacity-90" />
              {label}
            </motion.span>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.28 }}
        className="relative z-10 mt-auto pt-10"
      >
        <motion.div
          animate={reduceMotion ? undefined : { y: [0, -5, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          className="auth-glass max-w-md rounded-2xl p-6"
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-white/60">Team pulse</p>
              <p className="font-display text-lg font-semibold text-white">Weekly accountability</p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-white">
              <TrendingUp className="h-5 w-5" />
            </div>
          </div>

          <div className="mt-5 flex items-end justify-between gap-1.5" style={{ height: "5.5rem" }}>
            {BAR_HEIGHTS.map((h, i) => (
              <div key={WEEK_LABELS[i]} className="flex flex-1 flex-col items-center gap-1.5">
                <motion.div
                  className="w-full max-w-[1.75rem] rounded-t bg-white/70"
                  initial={{ height: 0 }}
                  animate={{ height: `${h}%` }}
                  transition={{ delay: 0.35 + i * 0.07, duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
                />
                <span className="text-[10px] font-medium text-white/55">{WEEK_LABELS[i]}</span>
              </div>
            ))}
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {[
              { label: "On track", count: 12, className: "border-emerald-400/30 bg-emerald-500/15 text-emerald-100" },
              { label: "At risk", count: 3, className: "border-amber-400/30 bg-amber-500/15 text-amber-100" },
              { label: "Behind", count: 1, className: "border-red-400/30 bg-red-500/15 text-red-100" },
            ].map(({ label, count, className }, i) => (
              <motion.span
                key={label}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.55 + i * 0.08 }}
                className={cn("inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold", className)}
              >
                <Activity className="h-3 w-3" />
                {count} {label}
              </motion.span>
            ))}
          </div>
        </motion.div>

        <motion.div
          animate={reduceMotion ? undefined : { y: [0, -7, 0] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
          className="auth-glass absolute -right-2 top-5 hidden items-center gap-2 rounded-full px-3 py-2 xl:flex"
        >
          <CheckCircle2 className="h-4 w-4 text-emerald-300" />
          <span className="text-xs font-semibold text-white">94% logs submitted</span>
        </motion.div>

        <motion.div
          animate={reduceMotion ? undefined : { y: [0, 6, 0] }}
          transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="auth-glass absolute -bottom-4 left-6 hidden items-center gap-2 rounded-full px-3 py-2 xl:flex"
        >
          <Shield className="h-4 w-4 text-emerald-300" />
          <span className="text-xs font-semibold text-white">Secure session</span>
        </motion.div>
      </motion.div>

      <p className="relative z-10 mt-8 text-xs text-white/50">Virtual Peace of Mind · Healthcare workforce dashboard</p>
    </div>
  );
}

function UnifiedLoginForm({ onSuccess }: { onSuccess: (role: "admin" | "staff") => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError("");

    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();

    const adminRes = await api.admin.login(trimmedEmail, trimmedPassword);
    if (adminRes.success) {
      setLoading(false);
      onSuccess("admin");
      return;
    }

    const staffRes = await api.staff.login(trimmedEmail, trimmedPassword);
    setLoading(false);

    if (staffRes.success) {
      onSuccess("staff");
      return;
    }

    setError("Invalid email or password. Please try again.");
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className="w-full max-w-[420px]"
    >
      <div className="mb-8 lg:hidden">
        <BrandLogo size="lg" to={null} />
      </div>

      <div className="rounded-2xl border border-vpm-border-subtle bg-white p-8 shadow-auth ring-1 ring-black/[0.03]">
        <h2 className="font-display text-2xl font-semibold text-gray-900">Welcome back</h2>
        <p className="mt-2 text-sm leading-relaxed text-vpm-forest-muted">
          Sign in with your organization email. You&apos;ll land on the dashboard that matches your role.
        </p>

        <form onSubmit={(e) => void handleSubmit(e)} className="mt-7 space-y-5">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="email" className="input-label">
              Email address
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              className="input-field"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@organization.com"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="input-label">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                className="input-field pr-11"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 hover:text-gray-600"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full py-3">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
          </button>
        </form>
      </div>

      <p className="mt-6 text-center text-xs text-vpm-forest-muted">
        © {new Date().getFullYear()} Virtual Peace of Mind · Workforce Accountability Dashboard
      </p>
    </motion.div>
  );
}

export function AuthPage() {
  const navigate = useNavigate();
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    void (async () => {
      const adminRes = await api.admin.me();
      if (adminRes.success) {
        navigate("/admin/dashboard", { replace: true });
        return;
      }
      const staffRes = await api.staff.me();
      if (staffRes.success) {
        navigate("/staff/dashboard", { replace: true });
        return;
      }
      setCheckingSession(false);
    })();
  }, [navigate]);

  if (checkingSession) return <LoadingScreen message="Loading…" />;

  return (
    <div className="auth-bg flex min-h-screen">
      <div className="auth-panel-dark relative hidden w-[48%] overflow-hidden xl:w-[52%] lg:flex">
        <AuthShowcase />
      </div>

      <div className="auth-login-panel flex flex-1 flex-col items-center justify-center px-6 py-12 sm:px-10">
        <UnifiedLoginForm
          onSuccess={(role) => navigate(role === "admin" ? "/admin/dashboard" : "/staff/dashboard")}
        />
      </div>
    </div>
  );
}
